/**
 * AgentIcon Component
 *
 * Animated icon for AI agent states with 4 dots connected by lines.
 * Inspired by ethereal constellation/network designs.
 *
 * States:
 * - send: Chevron/play button shape (static)
 * - stop: Square shape (static)
 * - loading: Dots spinning with magnetic collision effect
 * - thinking: Extends loading - dots orbit AND move in/out radially (through center)
 * - agent: Robot face (static) - rectangle head with eyes and antenna
 *
 * Transitions always go through center: converge → expand
 */

import React, { useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export type AgentIconState = 'send' | 'stop' | 'loading' | 'thinking' | 'agent';

export interface AgentIconProps {
  state: AgentIconState;
  size?: number;
  className?: string;
}

type Position = { x: number; y: number };

// Constants
const CENTER = 12;
const TRANSITION_MS = 300;
const LOADING_RADIUS = 8;
const DOT_RADIUS = 1.25;
const LINE_WIDTH = 1;
const LINE_OPACITY = 0.6;
const DOT_COLOR = 'var(--stone-500)';
const LINE_COLOR = 'var(--stone-400)';
const MAX_OSCILLATION = 0.35;

// Animation constants - defined once, never recreated
const OSCILLATION_PHASES = [0, 0.7, 1.4, 2.1] as const;
const BASE_OFFSETS = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2] as const;
const BASE_OSCILLATION_SPEEDS = [1.3, 1.7, 1.1, 1.5] as const;
const BASE_RADIUS_SPEEDS = [1.8, 2.3, 1.5, 2.0] as const;

const CENTER_POSITION: Position = { x: CENTER, y: CENTER };
const CENTER_POSITIONS: Position[] = Array(4).fill(CENTER_POSITION);
const ZERO_RADII = [0, 0, 0, 0] as const;

const ROBOT_EYE_POSITIONS: Position[] = [
  { x: 8.5, y: 12 },
  { x: 15.5, y: 12 },
];
const ROBOT_ANTENNA_POSITIONS: Position[] = [
  { x: 12, y: 7 },
  { x: 8, y: 1 },
];
const ROBOT_EXTRA_CENTER: Position[] = Array(4).fill(CENTER_POSITION);
const FULL_RADII = [
  LOADING_RADIUS,
  LOADING_RADIUS,
  LOADING_RADIUS,
  LOADING_RADIUS,
] as const;

// Dot positions for each static state (in 24x24 viewBox)
const STATE_POSITIONS: Record<AgentIconState, Position[]> = {
  send: [
    { x: 6, y: 4 },
    { x: 19, y: 12 },
    { x: 6, y: 20 },
    { x: 10, y: 12 },
  ],
  stop: [
    { x: 6, y: 6 },
    { x: 18, y: 6 },
    { x: 18, y: 18 },
    { x: 6, y: 18 },
  ],
  loading: [
    { x: 12, y: 4 },
    { x: 20, y: 12 },
    { x: 12, y: 20 },
    { x: 4, y: 12 },
  ],
  thinking: [
    { x: 12, y: 4 },
    { x: 20, y: 12 },
    { x: 12, y: 20 },
    { x: 4, y: 12 },
  ],
  agent: [
    { x: 4, y: 7 },
    { x: 20, y: 7 },
    { x: 20, y: 19 },
    { x: 4, y: 19 },
  ],
};

// Angular velocities (rad/s) for each state
const ANGULAR_VELOCITY: Record<AgentIconState, number> = {
  loading: 2.0,
  thinking: 0.8,
  send: 0,
  stop: 0,
  agent: 0,
};

function getCirclePosition(angle: number, radius: number): Position {
  return {
    x: CENTER + Math.cos(angle) * radius,
    y: CENTER + Math.sin(angle) * radius,
  };
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function isAnimatedState(state: AgentIconState): boolean {
  return state === 'loading' || state === 'thinking';
}

function calculateAnglesFromPositions(positions: Position[]): number[] {
  return positions.map(
    (pos) => Math.atan2(pos.y - CENTER, pos.x - CENTER) + Math.PI / 2,
  );
}

function calculateRadiiFromPositions(positions: Position[]): number[] {
  return positions.map((pos) =>
    Math.sqrt((pos.x - CENTER) ** 2 + (pos.y - CENTER) ** 2),
  );
}

function getLoadingStartAngles(): number[] {
  return BASE_OFFSETS.map((baseOffset, i) => {
    const oscillation = Math.sin(OSCILLATION_PHASES[i]) * MAX_OSCILLATION;
    return baseOffset + oscillation;
  });
}

type TransitionPhase = 'idle' | 'converge' | 'expand';

interface TransitionConfig {
  startTime: number;
  phase: 'converge' | 'expand';
  startAngles: number[];
  startRadii: number[];
  targetRadii: number[];
  angularVelocity: number;
  isSpiral: boolean;
  startPositions?: Position[];
  targetPositions?: Position[];
}

interface AnimationState {
  globalAngle: number;
  lastTime: number;
  startTime: number;
  radiusPhases: number[];
  radiusSpeeds: number[];
  oscillationSpeeds: number[];
}

function createInitialAnimationState(): AnimationState {
  return {
    globalAngle: 0,
    lastTime: 0,
    startTime: 0,
    radiusPhases: [0, Math.PI, 2 * Math.PI, 3 * Math.PI],
    radiusSpeeds: [...BASE_RADIUS_SPEEDS],
    oscillationSpeeds: [...BASE_OSCILLATION_SPEEDS],
  };
}

export function AgentIcon({
  state,
  size = 24,
  className,
}: AgentIconProps): React.ReactElement {
  const [prevState, setPrevState] = useState<AgentIconState>(state);
  const [transitionPhase, setTransitionPhase] =
    useState<TransitionPhase>('idle');
  const [positions, setPositions] = useState<Position[]>(
    STATE_POSITIONS[state],
  );
  const [robotExtraPositions, setRobotExtraPositions] = useState<Position[]>(
    state === 'agent'
      ? [...ROBOT_EYE_POSITIONS, ...ROBOT_ANTENNA_POSITIONS]
      : ROBOT_EXTRA_CENTER,
  );

  const transitionRef = useRef<TransitionConfig | null>(null);
  const animationStateRef = useRef<AnimationState>(
    createInitialAnimationState(),
  );
  const transitionEndAnglesRef = useRef<number[] | null>(null);

  const currentAnglesRef = useRef<number[]>([...BASE_OFFSETS]);
  const currentRadiiRef = useRef<number[]>([...FULL_RADII]);

  // Handle state transitions
  useEffect(() => {
    if (state === prevState) return;

    const fromAnimated = isAnimatedState(prevState);

    // Get current state for transition
    let startAngles: number[];
    let startRadii: number[];

    if (prevState === 'loading') {
      startAngles = currentAnglesRef.current;
      startRadii = [...FULL_RADII];
    } else if (prevState === 'thinking') {
      startAngles = currentAnglesRef.current;
      startRadii = currentRadiiRef.current;
    } else {
      const prevPositions = STATE_POSITIONS[prevState];
      startAngles = calculateAnglesFromPositions(prevPositions);
      startRadii = calculateRadiiFromPositions(prevPositions);
    }

    // Start converge phase
    setTransitionPhase('converge');
    transitionRef.current = {
      startTime: performance.now(),
      phase: 'converge',
      startAngles,
      startRadii,
      targetRadii: [...ZERO_RADII],
      angularVelocity: ANGULAR_VELOCITY[prevState],
      isSpiral: fromAnimated,
      startPositions: fromAnimated ? undefined : STATE_POSITIONS[prevState],
      targetPositions: fromAnimated ? undefined : CENTER_POSITIONS,
    };

    const convergeDuration = TRANSITION_MS / 2;
    const expandDuration = state === 'thinking' ? 0 : TRANSITION_MS / 2;

    // Schedule expand phase (skipped for thinking - it goes straight to animation from center)
    const expandTimer =
      state === 'thinking'
        ? null
        : setTimeout(() => {
            const expandStartAngles =
              state === 'loading'
                ? getLoadingStartAngles()
                : calculateAnglesFromPositions(STATE_POSITIONS[state]);

            const expandTargetRadii =
              state === 'loading'
                ? [...FULL_RADII]
                : calculateRadiiFromPositions(STATE_POSITIONS[state]);

            setTransitionPhase('expand');
            transitionRef.current = {
              startTime: performance.now(),
              phase: 'expand',
              startAngles: expandStartAngles,
              startRadii: [...ZERO_RADII],
              targetRadii: expandTargetRadii,
              angularVelocity: ANGULAR_VELOCITY[state],
              isSpiral: state === 'loading',
              startPositions:
                state === 'loading' ? undefined : CENTER_POSITIONS,
              targetPositions:
                state === 'loading' ? undefined : STATE_POSITIONS[state],
            };
          }, convergeDuration);

    const completeTimer = setTimeout(() => {
      if (state === 'thinking') {
        setPositions(CENTER_POSITIONS);
      } else if (state === 'loading') {
        const expandTime = TRANSITION_MS / 2 / 1000;
        const expandAngularDelta = ANGULAR_VELOCITY[state] * expandTime;
        const expandStartAngles = getLoadingStartAngles();
        const finalAngles = expandStartAngles.map(
          (a) => a + expandAngularDelta,
        );
        transitionEndAnglesRef.current = finalAngles;
        setPositions(
          finalAngles.map((angle) =>
            getCirclePosition(angle - Math.PI / 2, LOADING_RADIUS),
          ),
        );
      } else {
        transitionEndAnglesRef.current = null;
        setPositions(STATE_POSITIONS[state]);
      }

      if (state === 'agent') {
        setRobotExtraPositions([
          ...ROBOT_EYE_POSITIONS,
          ...ROBOT_ANTENNA_POSITIONS,
        ]);
      } else {
        setRobotExtraPositions(ROBOT_EXTRA_CENTER);
      }

      setTransitionPhase('idle');
      transitionRef.current = null;
      setPrevState(state);
    }, convergeDuration + expandDuration);

    return () => {
      if (expandTimer) clearTimeout(expandTimer);
      clearTimeout(completeTimer);
    };
  }, [state, prevState]);

  // Transition animation loop
  useEffect(() => {
    if (transitionPhase === 'idle') return;

    let frameId: number;

    const animate = (): void => {
      const config = transitionRef.current;
      if (!config) return;

      const elapsed = performance.now() - config.startTime;
      const duration = TRANSITION_MS / 2;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      let newPositions: Position[];

      if (config.isSpiral) {
        const elapsedSeconds = elapsed / 1000;
        const angularDelta = config.angularVelocity * elapsedSeconds;

        newPositions = config.startAngles.map((startAngle, i) => {
          const currentAngle = startAngle + angularDelta;
          const currentRadius =
            config.startRadii[i] +
            (config.targetRadii[i] - config.startRadii[i]) * easedProgress;
          return getCirclePosition(currentAngle - Math.PI / 2, currentRadius);
        });
      } else if (config.startPositions && config.targetPositions) {
        newPositions = config.startPositions.map((fromPos, i) => ({
          x:
            fromPos.x +
            (config.targetPositions![i].x - fromPos.x) * easedProgress,
          y:
            fromPos.y +
            (config.targetPositions![i].y - fromPos.y) * easedProgress,
        }));
      } else {
        return;
      }

      setPositions(newPositions);

      const isToAgent =
        config.phase === 'expand' &&
        config.targetPositions === STATE_POSITIONS['agent'];
      const isFromAgent =
        config.phase === 'converge' &&
        config.startPositions === STATE_POSITIONS['agent'];

      if (isToAgent || isFromAgent) {
        const robotTargets = [
          ...ROBOT_EYE_POSITIONS,
          ...ROBOT_ANTENNA_POSITIONS,
        ];
        const newRobotPositions = ROBOT_EXTRA_CENTER.map((fromPos, i) => {
          const toPos = robotTargets[i];
          if (isToAgent) {
            return {
              x: fromPos.x + (toPos.x - fromPos.x) * easedProgress,
              y: fromPos.y + (toPos.y - fromPos.y) * easedProgress,
            };
          } else {
            return {
              x: toPos.x + (fromPos.x - toPos.x) * easedProgress,
              y: toPos.y + (fromPos.y - toPos.y) * easedProgress,
            };
          }
        });
        setRobotExtraPositions(newRobotPositions);
      }

      if (progress < 1) {
        frameId = requestAnimationFrame(animate);
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [transitionPhase]);

  // Loading animation loop
  useEffect(() => {
    if (state !== 'loading' || transitionPhase !== 'idle') return;

    let frameId: number;
    const animState = animationStateRef.current;

    // Initialize from transition end angles if available
    if (transitionEndAnglesRef.current) {
      const initialOscillation =
        Math.sin(OSCILLATION_PHASES[0]) * MAX_OSCILLATION;
      animState.globalAngle =
        transitionEndAnglesRef.current[0] -
        BASE_OFFSETS[0] -
        initialOscillation;
      transitionEndAnglesRef.current = null;
    } else {
      animState.globalAngle = 0;
    }
    animState.lastTime = 0;
    animState.startTime = 0;

    const animate = (time: number): void => {
      if (animState.startTime === 0) animState.startTime = time;
      if (animState.lastTime === 0) animState.lastTime = time;

      const delta = (time - animState.lastTime) / 1000;
      animState.lastTime = time;
      animState.globalAngle += delta * ANGULAR_VELOCITY.loading;

      const relativeTime = time - animState.startTime;

      const newAngles = BASE_OFFSETS.map((baseOffset, i) => {
        const oscillation =
          Math.sin(
            relativeTime * 0.002 * BASE_OSCILLATION_SPEEDS[i] +
              OSCILLATION_PHASES[i],
          ) * MAX_OSCILLATION;
        return animState.globalAngle + baseOffset + oscillation;
      });

      currentAnglesRef.current = newAngles;
      setPositions(
        newAngles.map((angle) =>
          getCirclePosition(angle - Math.PI / 2, LOADING_RADIUS),
        ),
      );

      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [state, transitionPhase]);

  // Thinking animation loop
  useEffect(() => {
    if (state !== 'thinking' || transitionPhase !== 'idle') return;

    let frameId: number;
    const animState = animationStateRef.current;

    // Randomize starting conditions for variety each time thinking starts
    animState.globalAngle = Math.random() * Math.PI * 2;
    animState.lastTime = 0;
    animState.startTime = 0;
    animState.radiusPhases = [0, 1, 2, 3].map(
      () => Math.floor(Math.random() * 4) * Math.PI,
    );
    animState.radiusSpeeds = BASE_RADIUS_SPEEDS.map(
      (s) => s * (0.7 + Math.random() * 0.6),
    );
    animState.oscillationSpeeds = BASE_OSCILLATION_SPEEDS.map(
      (s) => s * (0.7 + Math.random() * 0.6),
    );

    setPositions(CENTER_POSITIONS);

    const animate = (time: number): void => {
      if (animState.startTime === 0) animState.startTime = time;
      if (animState.lastTime === 0) animState.lastTime = time;

      const delta = (time - animState.lastTime) / 1000;
      animState.lastTime = time;
      animState.globalAngle += delta * ANGULAR_VELOCITY.thinking;

      const relativeTime = time - animState.startTime;

      const newAngles = BASE_OFFSETS.map((baseOffset, i) => {
        const oscillation =
          Math.sin(
            relativeTime * 0.002 * animState.oscillationSpeeds[i] +
              OSCILLATION_PHASES[i],
          ) * MAX_OSCILLATION;
        return animState.globalAngle + baseOffset + oscillation;
      });

      const newRadii = animState.radiusPhases.map(
        (phase, i) =>
          Math.sin(relativeTime * 0.002 * animState.radiusSpeeds[i] + phase) *
          LOADING_RADIUS,
      );

      currentAnglesRef.current = newAngles;
      currentRadiiRef.current = newRadii;

      setPositions(
        newAngles.map((angle, i) =>
          getCirclePosition(angle - Math.PI / 2, newRadii[i]),
        ),
      );

      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [state, transitionPhase]);

  // Update positions for static states
  useEffect(() => {
    if (transitionPhase === 'idle' && !isAnimatedState(state)) {
      setPositions(STATE_POSITIONS[state]);
    }
  }, [state, transitionPhase]);

  const renderState =
    state === prevState || transitionPhase === 'expand' ? state : prevState;

  const showRobotExtras =
    renderState === 'agent' ||
    (transitionPhase !== 'idle' &&
      (state === 'agent' || prevState === 'agent'));

  const leftEye = robotExtraPositions[0];
  const rightEye = robotExtraPositions[1];
  const antennaLower = robotExtraPositions[2];
  const antennaTop = robotExtraPositions[3];

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={cn('agent-icon', className)}
      data-state={state}
      aria-label={state === 'agent' ? 'AI bot logo' : `AI agent ${state} state`}
      style={{ overflow: 'visible' }}
    >
      {positions.map((pos, i) => {
        const nextPos = positions[(i + 1) % positions.length];
        return (
          <line
            key={`line-${i}`}
            x1={pos.x}
            y1={pos.y}
            x2={nextPos.x}
            y2={nextPos.y}
            stroke={LINE_COLOR}
            strokeWidth={LINE_WIDTH}
            strokeLinecap="round"
            opacity={LINE_OPACITY}
          />
        );
      })}
      {positions.map((pos, i) => (
        <circle
          key={`dot-${i}`}
          cx={pos.x}
          cy={pos.y}
          r={renderState === 'send' && i === 3 ? DOT_RADIUS * 0.7 : DOT_RADIUS}
          fill={renderState === 'send' && i === 3 ? LINE_COLOR : DOT_COLOR}
        />
      ))}
      {showRobotExtras && (
        <>
          <line
            key="eye-line"
            x1={leftEye.x}
            y1={leftEye.y}
            x2={rightEye.x}
            y2={rightEye.y}
            stroke={LINE_COLOR}
            strokeWidth={LINE_WIDTH}
            strokeLinecap="round"
            opacity={LINE_OPACITY}
          />
          <circle
            key="left-eye"
            cx={leftEye.x}
            cy={leftEye.y}
            r={DOT_RADIUS * 1.4}
            fill={DOT_COLOR}
          />
          <circle
            key="right-eye"
            cx={rightEye.x}
            cy={rightEye.y}
            r={DOT_RADIUS * 1.4}
            fill={DOT_COLOR}
          />
          <line
            key="antenna-vertical"
            x1={antennaLower.x}
            y1={antennaLower.y}
            x2={antennaTop.x}
            y2={antennaTop.y}
            stroke={LINE_COLOR}
            strokeWidth={LINE_WIDTH}
            strokeLinecap="round"
            opacity={LINE_OPACITY}
          />
          <circle
            key="antenna-lower"
            cx={antennaLower.x}
            cy={antennaLower.y}
            r={DOT_RADIUS}
            fill={DOT_COLOR}
          />
          <circle
            key="antenna-top"
            cx={antennaTop.x}
            cy={antennaTop.y}
            r={DOT_RADIUS}
            fill={DOT_COLOR}
          />
        </>
      )}
    </svg>
  );
}

export default AgentIcon;
